/**
 * Acquisition — Channel performance deep-dive
 * Data: GA4 Property 484092824 (Feb 26 – Mar 25, 2026)
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { staggerContainer, fadeInUp, pageTransition, pageInitial, pageAnimate } from '@/lib/motionVariants';
import { formatNumber, formatPercentRaw, formatDuration } from '@/lib/formatting';
import { TEXT, BORDER, BRAND, CHART_COLORS, getChartTooltip, cssVar } from '../charts/utils';
import { useChatStore } from '@/stores/chatStore';
import { useThemeKey } from '@/hooks/useThemeKey';
import ChannelDrillPanel from '../drill/ChannelDrillPanel';
import type { ChannelData } from '../drill/ChannelDrillPanel';
import DeltaIndicator from '../shared/DeltaIndicator';
import { ACQUISITION_KPIS, CHANNEL_COMPARISON } from '@/data/comparisonData';
import PageHeader from '../shared/PageHeader';
import { useComparisonPeriods } from '@/hooks/useComparisonPeriods';
import { useChannels } from '@/hooks/useGA4Data';

const askChat = (query: string, e?: React.MouseEvent) => {
  if (e) {
    useChatStore.getState().showAnalyzePrompt(
      query, '', (e.currentTarget as HTMLElement).getBoundingClientRect(),
    );
  } else {
    useChatStore.getState().sendAnalyzeQuery(query);
  }
};

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const CHANNELS = [
  { channel: 'Paid Social',    sessions: 29_131, engagedSessions: 17_631, engagementRate: 60.52, avgTime: 3,  eventsPerSession: 3.94, eventCount: 114_846, keyEvents: 51, keyEventRate: 0.11, share: 33.25 },
  { channel: 'Organic Search', sessions: 22_137, engagedSessions: 20_304, engagementRate: 91.72, avgTime: 44, eventsPerSession: 8.38, eventCount: 185_493, keyEvents: 16, keyEventRate: 0.05, share: 25.27 },
  { channel: 'Direct',         sessions: 15_236, engagedSessions: 13_720, engagementRate: 90.05, avgTime: 21, eventsPerSession: 6.49, eventCount: 98_814,  keyEvents: 17, keyEventRate: 0.07, share: 17.39 },
  { channel: 'Paid Search',    sessions: 11_654, engagedSessions: 10_891, engagementRate: 93.45, avgTime: 40, eventsPerSession: 8.13, eventCount: 94_743,  keyEvents: 24, keyEventRate: 0.14, share: 13.30 },
  { channel: 'Organic Social', sessions: 4_340,  engagedSessions: 4_259,  engagementRate: 98.13, avgTime: 22, eventsPerSession: 8.16, eventCount: 35_413,  keyEvents: 3,  keyEventRate: 0.05, share: 4.95 },
  { channel: 'Paid Other',     sessions: 3_630,  engagedSessions: 3_266,  engagementRate: 89.97, avgTime: 7,  eventsPerSession: 4.43, eventCount: 16_075,  keyEvents: 2,  keyEventRate: 0.03, share: 4.14 },
  { channel: 'Referral',       sessions: 1_223,  engagedSessions: 1_125,  engagementRate: 91.99, avgTime: 50, eventsPerSession: 7.69, eventCount: 9_404,   keyEvents: 5,  keyEventRate: 0.16, share: 1.40 },
  { channel: 'Cross-network',  sessions: 197,    engagedSessions: 184,    engagementRate: 93.40, avgTime: 45, eventsPerSession: 8.54, eventCount: 1_683,   keyEvents: 0,  keyEventRate: 0,    share: 0.22 },
  { channel: 'Email',          sessions: 42,     engagedSessions: 40,     engagementRate: 95.24, avgTime: 26, eventsPerSession: 6.62, eventCount: 278,     keyEvents: 0,  keyEventRate: 0,    share: 0.05 },
];

const NEW_USERS_BY_CHANNEL = [
  { channel: 'Paid Social', users: 27_845 },
  { channel: 'Direct', users: 13_027 },
  { channel: 'Organic Search', users: 12_581 },
  { channel: 'Paid Search', users: 8_501 },
  { channel: 'Organic Social', users: 4_017 },
  { channel: 'Paid Other', users: 3_485 },
  { channel: 'Referral', users: 845 },
];

const EngagementDot: React.FC<{ rate: number }> = ({ rate }) => {
  const colorClass = rate >= 90 ? 'bg-success' : rate >= 75 ? 'bg-warning' : 'bg-danger';
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${colorClass}`} />
      <span className="font-mono">{formatPercentRaw(rate)}</span>
    </span>
  );
};

const Acquisition: React.FC = () => {
  const themeKey = useThemeKey();
  const chartTooltip = getChartTooltip();
  const { compareEnabled, popTooltip, yoyTooltip } = useComparisonPeriods();

  // Live API data with hardcoded fallbacks
  const { data: liveChannels } = useChannels();
  const channelRowsRaw = liveChannels ?? CHANNELS;
  // Compute share % if not present (live API data doesn't include it)
  const totalSessions = channelRowsRaw.reduce((s, c) => s + c.sessions, 0);
  const channelRows = channelRowsRaw.map(c => ({
    ...c,
    share: 'share' in c ? (c as typeof CHANNELS[number]).share : (totalSessions > 0 ? Math.round((c.sessions / totalSessions) * 1000) / 10 : 0),
  }));

  // Channel drill panel state
  const [selectedChannel, setSelectedChannel] = useState<ChannelData | null>(null);
  const [drillOpen, setDrillOpen] = useState(false);
  const handleChannelClick = useCallback((channel: typeof channelRows[number]) => {
    setSelectedChannel(channel);
    setDrillOpen(true);
  }, []);
  const handleDrillClose = useCallback(() => {
    setDrillOpen(false);
    setSelectedChannel(null);
  }, []);
  const sessionMix = useMemo(() => ({
    labels: channelRows.slice(0, 7).map((c) => c.channel),
    datasets: [{
      data: channelRows.slice(0, 7).map((c) => c.sessions),
      backgroundColor: [CHART_COLORS[5], CHART_COLORS[2], BRAND.gold, CHART_COLORS[1], CHART_COLORS[4], CHART_COLORS[3], CHART_COLORS[6]],
      borderWidth: 0,
    }],
  }), [channelRows]);

  const newUsersBar = useMemo(() => ({
    labels: NEW_USERS_BY_CHANNEL.map((c) => c.channel),
    datasets: [{
      label: 'New Users',
      data: NEW_USERS_BY_CHANNEL.map((c) => c.users),
      backgroundColor: BRAND.gold,
      borderRadius: 4,
    }],
  }), []);

  const engagementComparison = useMemo(() => ({
    labels: channelRows.slice(0, 7).map((c) => c.channel),
    datasets: [
      {
        label: 'Engagement Rate',
        data: channelRows.slice(0, 7).map((c) => c.engagementRate),
        backgroundColor: channelRows.slice(0, 7).map((c) =>
          c.engagementRate >= 90 ? cssVar('--color-success', '#10b981') : c.engagementRate >= 75 ? cssVar('--color-warning', '#f59e0b') : cssVar('--color-danger', '#ef4444')
        ),
        borderRadius: 4,
      },
    ],
  }), [channelRows]);

  return (
    <motion.div className="space-y-6" initial={pageInitial} animate={pageAnimate} transition={pageTransition}>
      <PageHeader title="Acquisition" subtitle="Traffic sources, channels, and new user attribution | GA4 Property 484092824" />

      {/* Summary KPIs */}
      <motion.div className="grid grid-cols-2 md:grid-cols-4 gap-3" variants={staggerContainer} initial="hidden" animate="visible">
        <motion.div variants={fadeInUp}>
        <div className="bg-bg-1 border border-border rounded-xl p-4">
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Total Sessions</p>
          <p className="font-mono text-lg font-bold text-text">87.6K</p>
          {compareEnabled && <div className="mt-1"><DeltaIndicator current={ACQUISITION_KPIS.totalSessions.current} yoyPrior={ACQUISITION_KPIS.totalSessions.yoy} popPrior={ACQUISITION_KPIS.totalSessions.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} /></div>}
        </div>
        </motion.div>
        <motion.div variants={fadeInUp}>
        <div className="bg-bg-1 border border-border rounded-xl p-4">
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">New Users</p>
          <p className="font-mono text-lg font-bold text-text">71K</p>
          {compareEnabled && <div className="mt-1"><DeltaIndicator current={ACQUISITION_KPIS.newUsers.current} yoyPrior={ACQUISITION_KPIS.newUsers.yoy} popPrior={ACQUISITION_KPIS.newUsers.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} /></div>}
        </div>
        </motion.div>
        <motion.div variants={fadeInUp}>
        <div className="bg-bg-1 border border-border rounded-xl p-4">
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Channels</p>
          <p className="font-mono text-lg font-bold text-text">9</p>
        </div>
        </motion.div>
        <motion.div variants={fadeInUp}>
        <div className="bg-bg-1 border border-danger/30 rounded-xl p-4 cursor-pointer hover:border-danger/60 hover:bg-bg-2 transition-colors" onClick={(e) => askChat('Paid Social has 11.5K unengaged sessions — 39.5% waste rate. What targeting changes would fix this?', e)}>
          <p className="text-xs text-text-3 uppercase tracking-wider font-semibold mb-1">Paid Social Waste</p>
          <p className="font-mono text-lg font-bold text-danger">11.5K</p>
          {compareEnabled && <div className="mt-1"><DeltaIndicator current={ACQUISITION_KPIS.paidSocialWaste.current} yoyPrior={ACQUISITION_KPIS.paidSocialWaste.yoy} popPrior={ACQUISITION_KPIS.paidSocialWaste.pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} invertColor /></div>}
          <p className="text-xs text-text-3 mt-0.5">unengaged sessions</p>
        </div>
        </motion.div>
      </motion.div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <h3 className="font-display text-sm text-text-2 mb-4">Session Mix</h3>
          <div className="flex items-center justify-center" style={{ height: 260 }}>
            <Doughnut
              key={`session-mix-${themeKey}`}
              data={sessionMix}
              options={{
                responsive: true, maintainAspectRatio: false, cutout: '55%',
                plugins: {
                  legend: { position: 'bottom', labels: { color: TEXT.muted, padding: 10, usePointStyle: true, pointStyle: 'circle', font: { size: 10 } } },
                  tooltip: chartTooltip,
                },
              }}
            />
          </div>
        </div>

        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <h3 className="font-display text-sm text-text-2 mb-4">Engagement Rate by Channel</h3>
          <div style={{ height: 260 }}>
            <Bar
              key={`engagement-comparison-${themeKey}`}
              data={engagementComparison}
              options={{
                responsive: true, maintainAspectRatio: false, indexAxis: 'y',
                scales: {
                  x: { min: 0, max: 100, grid: { color: BORDER.default }, ticks: { color: TEXT.faint, font: { size: 10 }, callback: (v) => `${v}%` }, border: { display: false } },
                  y: { grid: { display: false }, ticks: { color: TEXT.muted, font: { size: 10 } }, border: { display: false } },
                },
                plugins: { legend: { display: false }, tooltip: chartTooltip },
              }}
            />
          </div>
        </div>

        <div className="bg-bg-1 border border-border rounded-xl p-6">
          <h3 className="font-display text-sm text-text-2 mb-4">New Users by First Channel</h3>
          <div style={{ height: 260 }}>
            <Bar
              key={`new-users-${themeKey}`}
              data={newUsersBar}
              options={{
                responsive: true, maintainAspectRatio: false, indexAxis: 'y',
                scales: {
                  x: { grid: { color: BORDER.default }, ticks: { color: TEXT.faint, font: { size: 10 }, callback: (v) => formatNumber(Number(v)) }, border: { display: false } },
                  y: { grid: { display: false }, ticks: { color: TEXT.muted, font: { size: 10 } }, border: { display: false } },
                },
                plugins: { legend: { display: false }, tooltip: chartTooltip },
              }}
            />
          </div>
        </div>
      </div>

      {/* Full Channel Table */}
      <div className="bg-bg-1 border border-border rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="font-display text-sm text-text-2">Traffic Acquisition — Full Channel Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-bg-2 text-text-3">
                <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Channel</th>
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Sessions</th>
                {compareEnabled && <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Change</th>}
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Share</th>
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Engaged</th>
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Eng. Rate</th>
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Avg Time</th>
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Events/Sess</th>
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Event Count</th>
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Key Events</th>
                <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Key Rate</th>
                <th className="px-4 py-3 w-8" />
              </tr>
            </thead>
            <tbody>
              {channelRows.map((row, idx) => (
                <tr key={idx} className={`group border-b border-border/50 hover:bg-bg-2 transition-colors cursor-pointer ${row.engagementRate < 75 ? 'bg-danger/3' : ''}`} onClick={() => handleChannelClick(row)}>
                  <td className="px-4 py-3 font-medium text-text">{row.channel}</td>
                  <td className="px-4 py-3 text-right font-mono text-text">{formatNumber(row.sessions)}</td>
                  {compareEnabled && (
                    <td className="px-4 py-3 text-right">
                      {CHANNEL_COMPARISON[row.channel] && (
                        <DeltaIndicator current={row.sessions} yoyPrior={CHANNEL_COMPARISON[row.channel].yoy} popPrior={CHANNEL_COMPARISON[row.channel].pop} popTooltip={popTooltip} yoyTooltip={yoyTooltip} />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right font-mono text-text-3">{row.share}%</td>
                  <td className="px-4 py-3 text-right font-mono text-text-2">{formatNumber(row.engagedSessions)}</td>
                  <td className="px-4 py-3 text-right"><EngagementDot rate={row.engagementRate} /></td>
                  <td className="px-4 py-3 text-right font-mono text-text-2">{formatDuration(row.avgTime)}</td>
                  <td className="px-4 py-3 text-right font-mono text-text-2">{row.eventsPerSession.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right font-mono text-text-2">{formatNumber(row.eventCount)}</td>
                  <td className="px-4 py-3 text-right font-mono text-text">{row.keyEvents}</td>
                  <td className="px-4 py-3 text-right font-mono text-text-2">{formatPercentRaw(row.keyEventRate, 2)}</td>
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="w-4 h-4 text-text-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Insight */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-danger/5 border border-danger/30 rounded-xl p-4 cursor-pointer hover:brightness-110 transition-all" onClick={(e) => askChat('Paid Social drives 33% of sessions but only 60.5% engagement with 3s avg time. How should we reallocate this budget?', e)}>
          <p className="text-sm font-semibold text-text mb-1">Paid Social Efficiency Problem</p>
          <p className="text-xs text-text-3 leading-relaxed">
            Paid Social drives 33% of sessions but only 60.5% engagement (vs 79.5% overall). Average time on site is 3 seconds — users bounce immediately. 11,500 sessions are completely unengaged. Consider tightening targeting or reallocating budget to Paid Search which converts at 2.4x the rate.
          </p>
        </div>
        <div className="bg-success/5 border border-success/30 rounded-xl p-4 cursor-pointer hover:brightness-110 transition-all" onClick={(e) => askChat('Organic + Paid Search drive 38.6% of sessions with 90%+ engagement. How can we scale these quality channels?', e)}>
          <p className="text-sm font-semibold text-text mb-1">Organic + Paid Search = Quality Leaders</p>
          <p className="text-xs text-text-3 leading-relaxed">
            Organic Search (91.7% engagement, 44s avg) and Paid Search (93.5% engagement, 40s avg) together drive 38.6% of sessions with the highest quality metrics. Paid Search also has the highest key event rate at 0.14%. Referral traffic (50s avg time, 0.16% key rate) is small but punches above its weight.
          </p>
        </div>
      </div>

      {/* Channel Drill Panel */}
      <ChannelDrillPanel channel={selectedChannel} open={drillOpen} onClose={handleDrillClose} />
    </motion.div>
  );
};

export default Acquisition;
